import React, { Suspense, useContext } from "react";
import {
    BrowserRouter,
    Routes,
    Route,
    Navigate,
    Outlet,
} from "react-router-dom";
import "./App.css";
import Header from "./Components/Header/Header";
import AdminLogin from "./Components/AdminLogin/AdminLogin";
import { AuthContext } from "./context/AuthContext";
import "antd/dist/reset.css";
import LoadingSpinner from "./Components/Loader/LoadingSpinner";
import Footer from "./Components/Footer/Footer";

// Lazy load components
const Dashboard = React.lazy(() => import("./Components/Dashboard/Dashboard"));
const RaceManagement = React.lazy(() =>
    import("./Components/RaceManagement/RaceManagement")
);
const ForgotPassword = React.lazy(() =>
    import("./Components/Password/ForgotPassword/ForgotPassword")
);
const ResetPassword = React.lazy(() =>
    import("./Components/Password/ResetPassword/ResetPassword")
);

const AdminList = React.lazy(() => import("./Components/Admins/AdminList"));
const Profile = React.lazy(() => import("./Components/Profile/Profile"));

// Race-students view — reached via Race Management's "Student Results"
const RaceStudents = React.lazy(() =>
    import("./Components/ViewActiveRaceStudents/ViewActiveRaceStudents")
);
const Students = React.lazy(() => import("./Components/Students/Students"));
const AdminDetail = React.lazy(() => import("./Components/AdminDetail/AdminPageShell/AdminPageShell"));
const AdminReport = React.lazy(() => import("./Components/Report/AdminReport/AdminReport"));
const SuperAdminReport = React.lazy(() => import("./Components/Report/SuperAdminReport/SuperAdminReport"));

// Sidebar has been retired — Header now owns all navigation.
const Layout = () => (
    <div className="app-container">
        <Header />
        <main className="main-content">
            <Outlet />
        </main>
        <Footer />
    </div>
);

const App = () => {
    const { user, loading, error } = useContext(AuthContext);
    const isSuperAdmin = user?.userRole === "superadmin";

    if (loading) return <LoadingSpinner />;
    if (error) {
        return (
            <div style={{ textAlign: "center", padding: "50px", color: "red" }}>
                <p>{error}</p>
                <button onClick={() => window.location.reload()}>Retry</button>
            </div>
        );
    }

    return (
        <BrowserRouter>
            <Suspense fallback={<LoadingSpinner />}>
                <Routes>
                    <Route path="/login" element={<AdminLogin />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />

                    {user ? (
                        <Route element={<Layout />}>
                            <Route index element={<Dashboard />} />

                            <Route path="/students" element={<Students />} />

                            <Route
                                path="/race-students"
                                element={<RaceStudents />}
                            />

                            <Route path="/profile" element={<Profile />} />

                            {!isSuperAdmin && (
                                <>
                                    <Route
                                        path="/race-management"
                                        element={<RaceManagement />}
                                    />

                                    <Route
                                        path="/report"
                                        element={<AdminReport />}
                                    />
                                </>
                            )}

                            {isSuperAdmin && (
                                <>
                                    <Route path="/customers" element={<AdminList />} />
                                    <Route path="/customers/:adminId" element={<AdminDetail />} />
                                    <Route path="/report" element={<SuperAdminReport />} />
                                </>
                            )}

                            <Route path="*" element={<Navigate to="/" />} />
                        </Route>
                    ) : (
                        <Route path="*" element={<Navigate to="/login" />} />
                    )}
                </Routes>
            </Suspense>
        </BrowserRouter>
    );
};

export default App;